import { Component, ContentChild, HostListener, Inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog as MatDialog, MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { MODAL_EDITOR_TOKEN, ModalEditorBase } from '../../classes/modal-editor-base';

@Component({
    selector: 'ft-modal-editor-wrapper',
    templateUrl: './modal-editor-wrapper.component.html',
    styles: [':host {display: block}'],
    standalone: false
})
export class ModalEditorWrapperComponent {
    @ContentChild('triggerTmpl', { static: false }) triggerTmpl: TemplateRef<any>;
    @ViewChild('dialogTmpl', { static: false }) dialogTmpl: TemplateRef<any>;
    @Input() theme: 'dark' | 'light' | 'gray' = 'dark';
    @Input() canSave = true;
    @Input() saveDisabled = false;
    @Input() hasCloseButton = false;
    @Input() id = null;
    dialogRef: MatDialogRef<any>;
    isLoading = false;
    successSubscription: Subscription;
    errorSubscription: Subscription;

    constructor(protected dialog: MatDialog,
                @Inject(MODAL_EDITOR_TOKEN) public editorBase: ModalEditorBase) {
    }


    @HostListener('click', ['$event'])
    openDialog(e: MouseEvent) {
        e.stopPropagation();
        this.dialogRef = this.dialog.open(this.dialogTmpl, {
            panelClass: 'ft2',
            id: this.id,
            backdropClass: `-${this.theme}`,
            data: this
        });
        this.dialogRef.afterOpened().subscribe(() => {
            this.editorBase.visibilityChanged(true);

        });
        this.dialogRef.afterClosed().subscribe(() => {
            this.editorBase.visibilityChanged(false);
            // Reset the in-flight state WITH the dialog. A save that is dismissed mid-flight would
            // otherwise leave isLoading true on this wrapper for the life of the page: the next open
            // would show a spinning, permanently disabled Save button, and cancelChangesUnlessSaving
            // below would then refuse to revert on a cancel that has nothing in flight at all.
            this.isLoading = false;
            this.successSubscription?.unsubscribe();
            this.errorSubscription?.unsubscribe();
        });
        this.dialogRef.keydownEvents()
            .pipe(
                filter(event => this.editorBase.visible && (event.key === 'Escape'))
            )
            .subscribe(() => {
                this.cancelChangesUnlessSaving();
                this.editorBase.onCancel();
            });
        this.editorBase.updateInitialValue();
    }

    /**
     * Revert the editor to its pre-open snapshot — UNLESS a save is already in flight.
     *
     * Cancelling mid-save used to revert anyway, and onCancelChanges does not just reset the local
     * value: it calls onChangeFn(initialValue), pushing the stale snapshot back out through ngModel to
     * the parent. The save then succeeds, so the server, the store and the summary row all hold the NEW
     * value while the editor holds the OLD one — and because updateInitialValue re-snapshots on the next
     * open, pressing Save again silently reverts a change that had already been saved.
     *
     * The window only exists when saveSuccess$ is bound (onSave then holds the dialog open instead of
     * closing synchronously), so this affects every editor that binds it — create-account,
     * create-property, experience-form, mission-control-booking and the consent editor.
     *
     * Dismissal itself stays possible on purpose. Blocking it via disableClose would trap the user in a
     * modal whenever an outcome never arrives, which is a worse failure than a discarded cancel; the
     * value simply keeps whatever is being saved, which is what the server is about to agree with.
     */
    private cancelChangesUnlessSaving() {
        if (this.isLoading) {
            return;
        }
        this.editorBase.onCancelChanges();
    }

    onSave() {
        this.editorBase.applyChanges();
        if (this.editorBase.saveSuccess$) {
            this.isLoading = true;
            this.handleSubscriptions();
        } else {
            this.dialogRef.close();
        }
    }

    handleSubscriptions() {
        this.onSuccess();
        this.onError();
    }

    onSuccess() {
        this.successSubscription = this.editorBase.saveSuccess$.pipe(first()).subscribe(() => {
            this.dialogRef.close();
            this.isLoading = false;
        });
    }

    onError() {
        this.errorSubscription = this.editorBase.error$?.pipe(first()).subscribe(() => {
            this.successSubscription.unsubscribe();
            this.isLoading = false;
        });
    }

    onClose() {
        this.cancelChangesUnlessSaving();
        this.editorBase.onCancel();
        this.dialogRef.close();
    }
}
